import type { Basic, DiagnosticReport } from 'fhir/r4';
import { gql } from 'graphql-request';
import type { GraphqlConnectionNode, GraphqlPageInfo } from '../client';
import {
  fragmentBasic,
  fragmentCoding,
  fragmentOrganization,
  fragmentPatient,
  fragmentPractitioner,
} from './fragments';

export interface DiagnosticReportConnection {
  pageInfo: GraphqlPageInfo;
  edges: Array<GraphqlConnectionNode<DiagnosticReportWithBasics>>;
}

export type DiagnosticReportWithBasics = DiagnosticReport & {
  BasicList: Array<Basic>;
};

export interface DiagnosticReportGraphqlResponse {
  DiagnosticReportConnection: DiagnosticReportConnection;
}

/**
 * Lightweight fragment for list rendering. Omits:
 *  - `performer.resource` (Practitioner fragment) — only used by the drawer's Performer field
 *  - `subject.resource` (Patient + Organization fragments) — only used by the drawer's
 *    Data Source field via patientOrganizationName
 *  - `status`, `category`, `conclusion` — not read by any list column, sort, filter, or getter
 *  - top-level `extension { url, valueString }` — would only feed FHIRModel.patientUPID,
 *    which is never read for DR
 *  - `effectivePeriod.end` — only `start` is read by DiagnosticReportModel.date
 *
 * Use {@link getDiagnosticReportQuery} for the drawer where the extra fields are needed.
 */
const fragmentDiagnosticReportOverview = gql`
  ${fragmentCoding}
  ${fragmentBasic}
  fragment DiagnosticReportOverview on DiagnosticReport {
    id
    resourceType
    BasicList(_reference: "subject") {
      ...Basic
    }
    meta {
      tag {
        system
        code
      }
      extension {
        url
        valueInstant
      }
    }
    code {
      text
      coding {
        ...Coding
      }
    }
    effectiveDateTime
    effectivePeriod {
      start
    }
    issued
    # Reference-only versions of performer and subject so the drawer's
    # transient loading state (before useDiagnosticReportById refetches with the
    # full fragment) can still render DiagnosticReportModel.performer via its
    # display fallback. patientOrganizationName still needs the inlined Patient
    # resource, which the drawer's full-query refetch supplies.
    performer {
      reference
      display
    }
    subject {
      reference
    }
    result {
      reference
      display
    }
  }
`;

export const getDiagnosticReportOverviewQuery = gql`
  ${fragmentDiagnosticReportOverview}
  query DiagnosticReportsOverview(
    $upid: ID!
    $cursor: String!
    $sort: DiagnosticReportSortParams!
    $filter: DiagnosticReportFilterParams!
    $first: Int!
  ) {
    DiagnosticReportConnection(
      upid: $upid
      after: $cursor
      filter: $filter
      sort: $sort
      first: $first
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ...DiagnosticReportOverview
        }
      }
    }
  }
`;

export const getDiagnosticReportQuery = gql`
  ${fragmentCoding}
  ${fragmentPractitioner}
  ${fragmentBasic}
  ${fragmentOrganization}
  ${fragmentPatient}
  query DiagnosticReports(
    $upid: ID!
    $cursor: String!
    $sort: DiagnosticReportSortParams!
    $filter: DiagnosticReportFilterParams!
    $first: Int!
  ) {
    DiagnosticReportConnection(
      upid: $upid
      after: $cursor
      filter: $filter
      sort: $sort
      first: $first
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          resourceType
          BasicList(_reference: "subject") {
            ...Basic
          }
          meta {
            tag {
              system
              code
            }
            extension {
              url
              valueInstant
            }
          }
          extension {
            url
            valueString
          }
          status
          category {
            text
            coding {
              ...Coding
            }
          }
          code {
            text
            coding {
              ...Coding
            }
          }
          effectiveDateTime
          effectivePeriod {
            start
            end
          }
          issued
          conclusion
          performer {
            reference
            resource {
              ...Practitioner
            }
          }
          subject {
            reference
            resource {
              ...Patient
            }
          }
          result {
            reference
            display
          }
        }
      }
    }
  }
`;
